/**
 * The statewide parcel search contract — criteria, results, limits and gating.
 *
 * **This file is the single source of truth for both ends of the wire.** The
 * Cloudflare worker in `workers/state-search` imports it directly (relative
 * path, listed in that package's tsconfig `include`), so the request shape, the
 * validation rules and the plan gating cannot drift between client and server
 * the way a hand-mirrored copy would. That is also why nothing here may import
 * from `@/` or touch the DOM: the worker has neither.
 *
 * Adding a filter — `isWaterfront`, a zoning class, a sale-date window — means
 * editing three co-located things in this file (the `StatewideCriteria` field,
 * its entry in `CRITERIA_FIELDS`, and a line in the worker's predicate builder)
 * rather than hunting through the UI. Everything else — URL round-tripping, the
 * "is this searchable" check, the chip labels, the gated-field strip — derives
 * from `CRITERIA_FIELDS`.
 */

import { type Feature, type Plan, planAllows } from "./entitlements";
import { TRANSMISSION_MAX_MILES, WATER_FILTER_TYPES, hasLayerFilters, type LayerFilters, type WaterFilterType } from "./parcel-layers";
import {
  PO_BOX_SPELLINGS,
  ROUTE_DESIGNATIONS,
  expandAddressToken,
  isRouteToken,
  normalizeAddressQuery,
  normalizeOwnerTokens,
  ownerTokenSpellings,
} from "./search-normalize";

/**
 * The request/response shape this client and worker agree on.
 *
 * Stamped on every worker response, checked on every client response. It
 * exists because the frontend and the worker deploy *separately* — Pages
 * auto-deploys on push, the worker needs its own `wrangler deploy` — so the
 * two can be out of step for as long as nobody notices. When the overhaul
 * shipped, the new client's `{criteria, sort, dir}` body reached the old
 * worker, which read `payload.query`, found nothing, and answered
 * "Query must include letters or numbers". The UI faithfully reported that as
 * a search with no matches, and the actual cause — a worker two versions
 * behind — was invisible.
 *
 * Bump this whenever the request body or response shape changes in a way an
 * older worker would mishandle.
 */
export const STATE_SEARCH_API_VERSION = 6;

/**
 * The first worker that reads the map-layer criteria. An older one ignores
 * them and answers with the unfiltered search, which would read as "these are
 * the lakefront parcels"; the client refuses such an answer instead.
 */
export const LAYER_CRITERIA_API_VERSION = 5;

/** Minimum alphanumeric characters in a free-text term before it is searchable. */
export const MIN_TERM_LENGTH = 3;

/**
 * Most rows one search returns.
 *
 * The response also carries an exact `totalMatches`, so a capped result set can
 * say "showing 1,000 of 4,812" instead of the old "500+ — too many to show".
 * Knowing the real number is what tells someone whether to narrow or to give up.
 */
export const RESULT_CAP = 1000;

/** Rows per page in the results panel. */
export const PAGE_SIZE = 25;

/**
 * What a statewide search filters on.
 *
 * **Market value is deliberately absent.** Only 55% of parcels carry an
 * assessed value, so a value range silently discards ~1.6M parcels, and the
 * values that do exist are assessment-roll figures that lag boundary changes
 * badly (a parcel assessed at 15.7 acres against 73.3 deeded — see the RETR
 * notes in AGENTS.md). A filter that quietly drops nearly half the state on
 * numbers that stale is worse than no filter. The `market_value` column is
 * still *shown* in results and exported, where it is clearly one attribute
 * among several rather than the thing that decided what you did and did not
 * see. `filterValue` remains a real Lite feature in the county view, where the
 * whole dataset is in the browser and an empty value is visible as an empty
 * cell.
 */
export interface StatewideCriteria {
  /** Owner name tokens, matched word-prefix and order-independent. */
  owner: string;
  /**
   * Address tokens, matched word-prefix with USPS suffix synonyms against the
   * parcel's site address *and* its owner's mailing address; see
   * `StatewideResult.mailing_match`.
   */
  address: string;
  /** County slugs (the `county_slug` column / `COUNTIES` keys). Empty = all 72. */
  counties: string[];
  /** City / town / village, matched word-prefix against `place_name`. */
  place: string;
  /** ZIP, matched as a prefix so "537" works. */
  zip: string;
  acresMin: number | null;
  acresMax: number | null;
  /**
   * "Other parcels mailed to this owner": a `county_slug/parcel_id` reference
   * (`mailOfRef`) to the parcel the search started from. The worker reads
   * that parcel's owner and mailing address and matches *mailing addresses
   * only* — see `MailingSource`. A reference rather than the address text,
   * because 32 counties publish only the street line ("216 GRAND ST") and
   * the street alone cannot tell Winona's 216 Grand St from Shawano's; the
   * owner's name is what can, and it stays out of the URL this way.
   */
  mailOf: string;
  /**
   * The county map's water and environment filters, Pro (`mapLayers`), with
   * the same meanings (`layerMatcher` in parcel-layers.ts; the SQL is
   * parcel-layer-sql.ts, shared with the AI tools).
   *
   * Water: empty is off, `["any"]` any water, otherwise any of these types
   * (`WATER_FILTER_TYPES`).
   */
  water: string[];
  /** Least shore frontage, in feet. Only with `water`. */
  frontage: number | null;
  /**
   * Words of a water body's name, "Pewaukee Lake". Only with `water`. A name
   * rather than the county map's DNR id: statewide there is no county list
   * to pick it from, and the words must all name the same water body.
   */
  waterName: string;
  /** In, or wholly outside, FEMA's 100-year floodplain; empty is off. */
  flood: "" | "in" | "out";
  /** Some mapped wetland, or none at all; empty is off. */
  wetland: "" | "some" | "none";
  /** Least share of the parcel mapped as wetland, in percent. Only with `wetland: "some"`. */
  wetlandPct: number | null;
  /** Within this many miles of the proposed 765-kV line (at most 2); 0 is "crosses it". */
  tx765: number | null;
}

export const EMPTY_CRITERIA: StatewideCriteria = {
  owner: "",
  address: "",
  counties: [],
  place: "",
  zip: "",
  acresMin: null,
  acresMax: null,
  mailOf: "",
  water: [],
  frontage: null,
  waterName: "",
  flood: "",
  wetland: "",
  wetlandPct: null,
  tx765: null,
};

/** The `mailOf` value for a parcel. `county_slug` never contains a slash. */
export function mailOfRef(countySlug: string, parcelId: string): string {
  return `${countySlug}/${parcelId}`;
}

/** Split a `mailOf` value, or null when it is not one. */
export function parseMailOfRef(ref: string): { countySlug: string; parcelId: string } | null {
  const slash = ref.indexOf("/");
  if (slash <= 0) return null;
  const countySlug = ref.slice(0, slash);
  const parcelId = ref.slice(slash + 1);
  if (!/^[a-z0-9_]+$/.test(countySlug) || !parcelId) return null;
  return { countySlug, parcelId };
}

/**
 * The parcel a `mailOf` search starts from, as the worker read it. The
 * builder matches its street line against every mailing address, then asks
 * each match for evidence it is the same mailbox: the same mailing ZIP where
 * both addresses carry one, otherwise a shared owner name.
 */
export interface MailingSource {
  mailAddress: string;
  owner: string;
}

/** One parcel as the search returns it. */
export interface StatewideResult {
  parcel_id: string;
  /** The county's own tax number where that county is migrated; see CLAUDE.md. */
  tax_parcel_id: string | null;
  owner: string;
  address: string | null;
  /** Null on a plan without `searchMailingStatewide`, which is not sent it (v6+). */
  mail_address: string | null;
  /**
   * Set only where the plan withheld `mail_address` (v6+): whether the
   * address it had can find the owner's other parcels (a house or box
   * number), so the Lite offer is shown where Lite would have a search to run.
   */
  mail_searchable?: boolean;
  /**
   * True when an address search matched this parcel only through its owner's
   * mailing address, not where the land is. The UI must say so: the row's own
   * address is somewhere else, or absent. Missing from workers before v3.
   */
  mailing_match?: boolean;
  /** County display name, e.g. "DANE". */
  county: string;
  /** County slug, the key into `COUNTIES` — use this for links, never the name. */
  county_slug: string;
  place: string | null;
  zip: string | null;
  school_dist: string | null;
  acres: number | null;
  market_value: number | null;
  lat: number;
  lon: number;
  /**
   * What the map layers say about the parcel, Pro only (v5+): the
   * `parcel_layers` columns that are set, named as `ParcelLayerRow` names
   * them. `null` is looked up and no layer touches it (no mapped water,
   * wetland or floodplain, and over 2 mi from the 765-kV line); absent is
   * not looked up, which is every row for other plans.
   */
  layers?: StatewideLayerFacts | null;
}

/** The `parcel_layers` columns a result carries, in `ParcelLayerRow`'s names. */
export const LAYER_FACT_KEYS = [
  "waterfront_type",
  "waterfront_ft",
  "primary_water_name",
  "primary_water_wbic",
  "water_bodies",
  "primary_water_source",
  "on_lake",
  "on_pond",
  "on_river",
  "on_stream",
  "stream_through",
  "wetland_acres",
  "wetland_pct",
  "main_wetland_type",
  "wetland_types",
  "flood_acres",
  "flood_pct",
  "flood_zones",
  "in_floodway",
  "tx765_nearest_ft",
  "tx765_maribell_ft",
  "tx765_beci_ft",
  "tx765_beci_segment",
  "tx765_crossed_by",
] as const;

/** The set `parcel_layers` columns of one parcel; see `StatewideResult.layers`. */
export type StatewideLayerFacts = Partial<Record<(typeof LAYER_FACT_KEYS)[number], string | number | boolean>>;

export type StatewideSortKey =
  | "relevance"
  | "owner"
  | "address"
  | "place"
  | "county"
  | "acres"
  | "value";

export type SortDirection = "asc" | "desc";

/**
 * Sorting happens server-side, before the cap is applied.
 *
 * Sorting the capped page in the browser would be a lie whenever the match
 * count exceeds `RESULT_CAP`: "sort by acres descending" would return the
 * largest parcel out of an arbitrary 1,000, not out of all 4,812. Re-sorting
 * therefore re-queries — which is free, because the daily cap counts *distinct
 * criteria*, not requests (see `criteriaKey`).
 */
export const SORT_KEYS: readonly StatewideSortKey[] = [
  "relevance",
  "owner",
  "address",
  "place",
  "county",
  "acres",
  "value",
] as const;

export interface StatewideSearchRequest {
  criteria: StatewideCriteria;
  sort: StatewideSortKey;
  dir: SortDirection;
}

export interface StatewideSearchResponse {
  /** Always `STATE_SEARCH_API_VERSION`; absent means the worker is stale. */
  apiVersion: number;
  results: StatewideResult[];
  /** Rows actually returned (at most `RESULT_CAP`). */
  resultCount: number;
  /** Exact number of parcels matching, however many were returned. */
  totalMatches: number;
  /** True when `totalMatches > resultCount`. */
  hasMore: boolean;
  /** How many of `totalMatches` matched by mailing address only (v3+). */
  mailingMatches?: number;
  /**
   * A plan without `searchMailingStatewide` only (v6+): how many parcels the
   * address matched where the owner gets mail, which it is not sent.
   * `totalMatches` and `results` are the site matches alone.
   */
  lockedMailingMatches?: number;
  /** The address a `mailOf` search matched on, for its chip (v4+). */
  mailedTo?: string | null;
  resultCap: number;
  /** Distinct searches this user has run today, or null when uncapped. */
  searchesToday: number | null;
  /** The cap in force, or null when searches are unlimited for this user. */
  dailyLimit: number | null;
  /** The caller's plan, as the server resolved it. */
  plan: Plan;
  /**
   * Labels of filters the server dropped because the plan does not include
   * them. Present so the UI can say so rather than quietly returning
   * differently-filtered results than the person asked for.
   */
  strippedFilters: string[];
  /**
   * What the person needs to know about the map-layer filters as applied
   * (v5+): the counties a floodplain filter left out, or could not apply in.
   */
  layerNotes?: string[];
}

/**
 * What a plan without `searchMailingStatewide` is told about the `mailOf`
 * search it asked for (v6+): a 403 with `code: "mailing_locked"`, the counts
 * and no rows. It spends no free search.
 */
export interface MailingPreview {
  /** Parcels statewide the search would list, the one it started from included. */
  parcels: number;
  /** How many counties they are in. */
  counties: number;
}

/** A criteria field, and everything the app needs to know about how to treat it. */
interface CriteriaField {
  key: keyof StatewideCriteria;
  /** Human label, used in chips and in the "not on your plan" notice. */
  label: string;
  /** `choice` is one of a few fixed words (`choices`), never free text. */
  kind: "text" | "list" | "number" | "choice";
  /** The values a `choice` field may take. */
  choices?: readonly string[];
  /** URL query parameter. */
  param: string;
  /**
   * True when this field can stand alone as a search. A search needs at least
   * one anchor — a bare acreage range would match a million parcels and is a
   * table scan with nothing to narrow it. A function decides by value:
   * "in the floodplain" narrows, "outside it" is most of the state.
   */
  anchor: boolean | ((criteria: StatewideCriteria) => boolean);
  /** Plan gate, when the field is not available on every plan. */
  feature?: Feature;
  /** Fields that share a gate and a label are reported, and relaxed, together. */
  group?: string;
  /** A min/max pair, written to the search log as one `group:min-max` entry. */
  range?: true;
  /**
   * Left out of `criteriaKey` while unset. Every field added after the cap
   * began counting keys carries it, so a search run before the field existed
   * keeps its key, and a replay of it costs nothing.
   */
  keyWhenSet?: true;
}

export const CRITERIA_FIELDS: readonly CriteriaField[] = [
  { key: "owner", label: "Owner", kind: "text", param: "owner", anchor: true },
  { key: "address", label: "Address", kind: "text", param: "addr", anchor: true },
  { key: "counties", label: "County", kind: "list", param: "county", anchor: true },
  { key: "place", label: "City/town", kind: "text", param: "place", anchor: true },
  { key: "zip", label: "ZIP", kind: "text", param: "zip", anchor: true },
  { key: "mailOf", label: "Mailed to", kind: "text", param: "mailof", anchor: true },
  {
    key: "acresMin",
    label: "Acreage range",
    kind: "number",
    param: "minAcres",
    anchor: false,
    feature: "filterAcres",
    group: "acres",
    range: true,
  },
  {
    key: "acresMax",
    label: "Acreage range",
    kind: "number",
    param: "maxAcres",
    anchor: false,
    feature: "filterAcres",
    group: "acres",
    range: true,
  },
  // The county map's water and environment filters (Pro). The URL parameters
  // are the county map's (`LAYER_PARAM_KEYS`), so the same words mean the same
  // filter on both pages; `waterName` is statewide's own, since the county
  // map picks a water body by DNR id from its own list. Each anchors a search
  // when it narrows one, as in the AI tools: "outside the floodplain" or "no
  // wetland" is most of the state.
  { key: "water", label: "Water", kind: "list", param: "water", anchor: true, feature: "mapLayers", group: "water", keyWhenSet: true },
  { key: "waterName", label: "Water", kind: "text", param: "waterName", anchor: true, feature: "mapLayers", group: "water", keyWhenSet: true },
  { key: "frontage", label: "Water", kind: "number", param: "frontage", anchor: false, feature: "mapLayers", group: "water", keyWhenSet: true },
  {
    key: "flood",
    label: "Floodplain",
    kind: "choice",
    choices: ["in", "out"],
    param: "flood",
    anchor: (criteria) => criteria.flood === "in",
    feature: "mapLayers",
    keyWhenSet: true,
  },
  {
    key: "wetland",
    label: "Wetland",
    kind: "choice",
    choices: ["some", "none"],
    param: "wetland",
    anchor: (criteria) => criteria.wetland === "some",
    feature: "mapLayers",
    group: "wetland",
    keyWhenSet: true,
  },
  { key: "wetlandPct", label: "Wetland", kind: "number", param: "wetlandPct", anchor: false, feature: "mapLayers", group: "wetland", keyWhenSet: true },
  { key: "tx765", label: "765-kV corridor", kind: "number", param: "tx765", anchor: true, feature: "mapLayers", keyWhenSet: true },
] as const;

function isFieldSet(criteria: StatewideCriteria, field: CriteriaField): boolean {
  const value = criteria[field.key];
  if (field.kind === "list") return Array.isArray(value) && value.length > 0;
  if (field.kind === "number") return typeof value === "number" && Number.isFinite(value);
  return typeof value === "string" && value.trim().length > 0;
}

/** The value a field takes when it is cleared. */
function emptyValue(field: CriteriaField): StatewideCriteria[keyof StatewideCriteria] {
  return EMPTY_CRITERIA[field.key];
}

function isAnchor(criteria: StatewideCriteria, field: CriteriaField): boolean {
  if (!isFieldSet(criteria, field)) return false;
  return typeof field.anchor === "function" ? field.anchor(criteria) : field.anchor;
}

/** True when the criteria carry any map-layer filter (Pro). */
export function hasLayerCriteria(criteria: StatewideCriteria): boolean {
  return CRITERIA_FIELDS.some((field) => field.feature === "mapLayers" && isFieldSet(criteria, field));
}

/**
 * The criteria's layer filters in the county map's shape, so the statewide
 * form can use the county sidebar's own controls (`LayerFilterControls`).
 * The water body's name has no place there; the form passes it beside.
 */
export function criteriaLayerFilters(criteria: StatewideCriteria): LayerFilters | undefined {
  const filters: LayerFilters = {};
  if (criteria.water.length > 0) {
    filters.water = {
      types: criteria.water.filter((type): type is WaterFilterType => type !== "any"),
      minFrontageFt: criteria.frontage ?? 0,
    };
  }
  if (criteria.flood) filters.flood = criteria.flood;
  if (criteria.wetland) filters.wetland = { mode: criteria.wetland, minPct: criteria.wetlandPct ?? 0 };
  if (criteria.tx765 !== null) filters.tx765 = { withinMiles: criteria.tx765 };
  return hasLayerFilters(filters) ? filters : undefined;
}

/** The criteria with the county-map-shaped layer filters written back in. */
export function withLayerFilters(criteria: StatewideCriteria, filters: LayerFilters | undefined): StatewideCriteria {
  const water = filters?.water;
  return {
    ...criteria,
    water: water ? (water.types.length > 0 ? [...water.types] : ["any"]) : [],
    frontage: water && water.minFrontageFt > 0 ? water.minFrontageFt : null,
    waterName: water ? criteria.waterName : "",
    flood: filters?.flood ?? "",
    wetland: filters?.wetland?.mode ?? "",
    wetlandPct: filters?.wetland?.mode === "some" && filters.wetland.minPct > 0 ? filters.wetland.minPct : null,
    tx765: filters?.tx765 ? filters.tx765.withinMiles : null,
  };
}

function toTrimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 ? value : null;
  }

  // People type "$250,000" and "1,200 ac". Strip the formatting, but a string
  // with no digits left in it is *unset*, not zero — `Number("")` is 0, and an
  // accidental "n/a" silently becoming `value >= 0` would drop every parcel
  // with no assessed value on file.
  const digits = String(value).replace(/[^0-9.]/g, "");
  if (!digits || !/[0-9]/.test(digits)) return null;
  const parsed = Number(digits);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

const WATER_VALUES: readonly string[] = ["any", ...WATER_FILTER_TYPES];

/**
 * The layer half of `normalizeCriteria`. Each refinement keeps only the
 * filter it refines: a frontage or a water body name turns the water filter
 * on (any water) rather than being dropped, and a wetland share means "some
 * wetland". Values it cannot read are dropped, never guessed, as in the
 * county map's `decodeLayerFilters`.
 */
function normalizeLayerCriteria(
  source: Record<string, unknown>,
): Pick<StatewideCriteria, "water" | "frontage" | "waterName" | "flood" | "wetland" | "wetlandPct" | "tx765"> {
  const rawWater = Array.isArray(source.water) ? source.water : [];
  let water = Array.from(
    new Set(rawWater.map((value) => toTrimmedString(value).toLowerCase()).filter((value) => WATER_VALUES.includes(value))),
  );
  // "Any" beside a type would read as the type: the narrower choice wins.
  if (water.length > 1) water = water.filter((value) => value !== "any");
  const waterName = toTrimmedString(source.waterName).slice(0, 80);
  const frontageRaw = toNullableNumber(source.frontage);
  const frontage = frontageRaw !== null && frontageRaw > 0 ? Math.min(frontageRaw, 100_000) : null;
  if (water.length === 0 && (waterName || frontage !== null)) water = ["any"];

  const flood = toTrimmedString(source.flood).toLowerCase();
  let wetland = toTrimmedString(source.wetland).toLowerCase();
  const pctRaw = toNullableNumber(source.wetlandPct);
  const wetlandPct = pctRaw !== null && pctRaw > 0 ? Math.min(pctRaw, 100) : null;
  if (wetland !== "some" && wetland !== "none") wetland = wetlandPct !== null ? "some" : "";

  const txRaw = toNullableNumber(source.tx765);
  return {
    water,
    frontage: water.length > 0 ? frontage : null,
    waterName: water.length > 0 ? waterName : "",
    flood: flood === "in" || flood === "out" ? flood : "",
    wetland: wetland as StatewideCriteria["wetland"],
    wetlandPct: wetland === "some" ? wetlandPct : null,
    tx765: txRaw === null ? null : Math.min(txRaw, TRANSMISSION_MAX_MILES),
  };
}

/**
 * Coerce anything — a JSON request body, a restored URL, a stale localStorage
 * blob — into well-formed criteria. Never throws; unknown fields are dropped
 * and malformed values become empty rather than reaching the SQL builder.
 */
export function normalizeCriteria(raw: unknown): StatewideCriteria {
  const source = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;

  const counties = Array.isArray(source.counties)
    ? Array.from(
        new Set(
          source.counties
            .map((slug) => toTrimmedString(slug).toLowerCase().replace(/[^a-z0-9_]/g, ""))
            .filter(Boolean),
        ),
      ).slice(0, 72)
    : [];

  const criteria: StatewideCriteria = {
    owner: toTrimmedString(source.owner).slice(0, 120),
    address: toTrimmedString(source.address).slice(0, 120),
    counties,
    place: toTrimmedString(source.place).slice(0, 80),
    zip: toTrimmedString(source.zip).replace(/[^0-9]/g, "").slice(0, 5),
    acresMin: toNullableNumber(source.acresMin),
    acresMax: toNullableNumber(source.acresMax),
    mailOf: toTrimmedString(source.mailOf).slice(0, 160),
    ...normalizeLayerCriteria(source),
  };
  if (criteria.mailOf && !parseMailOfRef(criteria.mailOf)) criteria.mailOf = "";

  // An inverted range is a typo, not a request for zero rows. Swapping is what
  // the person meant and keeps the result set non-empty.
  if (criteria.acresMin !== null && criteria.acresMax !== null && criteria.acresMin > criteria.acresMax) {
    [criteria.acresMin, criteria.acresMax] = [criteria.acresMax, criteria.acresMin];
  }
  return criteria;
}

export function normalizeSort(raw: unknown): StatewideSortKey {
  const value = toTrimmedString(raw) as StatewideSortKey;
  return SORT_KEYS.includes(value) ? value : "relevance";
}

export function normalizeDirection(raw: unknown): SortDirection {
  return toTrimmedString(raw) === "desc" ? "desc" : "asc";
}

/** Alphanumeric length of a free-text term, ignoring punctuation and spacing. */
export function searchableLength(text: string): number {
  return text.replace(/[^a-z0-9]+/gi, "").length;
}

export interface CriteriaValidation {
  ok: boolean;
  /** Why the criteria cannot be searched, phrased for the person who typed them. */
  message?: string;
}

/**
 * Whether these criteria describe a search worth running.
 *
 * Two rules, both there to keep a query from degenerating into a full scan of
 * 3.5M rows with nothing to narrow it:
 *  1. at least one anchor field (owner, address, county, city or ZIP);
 *  2. free text long enough to mean something.
 */
/**
 * `anchored` says something outside the criteria already narrows the search —
 * the MCP server's radius around a point — so a size range alone is fine.
 */
export function validateCriteria(
  criteria: StatewideCriteria,
  options: { anchored?: boolean } = {},
): CriteriaValidation {
  for (const field of CRITERIA_FIELDS) {
    if (field.kind !== "text" || !isFieldSet(criteria, field)) continue;
    const term = criteria[field.key] as string;
    if (searchableLength(term) < MIN_TERM_LENGTH) {
      return {
        ok: false,
        message: `${field.key === "waterName" ? "A lake or river name" : field.label} needs at least ${MIN_TERM_LENGTH} letters or numbers.`,
      };
    }
  }

  const hasAnchor = options.anchored === true || CRITERIA_FIELDS.some((field) => isAnchor(criteria, field));
  if (!hasAnchor) {
    return {
      ok: false,
      message: hasLayerCriteria(criteria)
        ? "Add an owner, address, county, city or ZIP — outside the floodplain or with no wetland alone matches too much of the state."
        : "Add an owner, address, county, city or ZIP — a size or value range alone matches too much of the state.",
    };
  }

  return { ok: true };
}

/** True when any field at all is set — used to enable "Clear". */
export function hasAnyCriteria(criteria: StatewideCriteria): boolean {
  return CRITERIA_FIELDS.some((field) => isFieldSet(criteria, field));
}

export interface CriteriaChip {
  key: string;
  label: string;
  value: string;
  /** The fields this chip clears. */
  fields: (keyof StatewideCriteria)[];
}

function formatAcresValue(value: number): string {
  return value >= 100 ? value.toFixed(0) : String(value);
}

function formatRange(
  min: number | null,
  max: number | null,
  format: (value: number) => string,
  unit: string,
): string {
  if (min !== null && max !== null) return `${format(min)}–${format(max)}${unit}`;
  if (min !== null) return `${format(min)}${unit}+`;
  return `under ${format(max as number)}${unit}`;
}

/** The active criteria as removable chips, in the order they read best. */
export function describeCriteria(
  criteria: StatewideCriteria,
  countyLabel: (slug: string) => string = (slug) => slug,
  mailLabel: (ref: string) => string | null = () => null,
): CriteriaChip[] {
  const chips: CriteriaChip[] = [];

  if (criteria.mailOf) {
    chips.push({
      key: "mailOf",
      label: "Mailed to",
      value: mailLabel(criteria.mailOf) ?? "this owner's mailing address",
      fields: ["mailOf"],
    });
  }

  if (criteria.owner.trim()) {
    chips.push({ key: "owner", label: "Owner", value: criteria.owner.trim(), fields: ["owner"] });
  }
  if (criteria.address.trim()) {
    chips.push({ key: "address", label: "Address", value: criteria.address.trim(), fields: ["address"] });
  }
  if (criteria.counties.length > 0) {
    chips.push({
      key: "counties",
      label: criteria.counties.length === 1 ? "County" : "Counties",
      value:
        criteria.counties.length <= 2
          ? criteria.counties.map(countyLabel).join(", ")
          : `${criteria.counties.length} selected`,
      fields: ["counties"],
    });
  }
  if (criteria.place.trim()) {
    chips.push({ key: "place", label: "City/town", value: criteria.place.trim(), fields: ["place"] });
  }
  if (criteria.zip.trim()) {
    chips.push({ key: "zip", label: "ZIP", value: criteria.zip.trim(), fields: ["zip"] });
  }
  if (criteria.acresMin !== null || criteria.acresMax !== null) {
    chips.push({
      key: "acres",
      label: "Acres",
      value: formatRange(criteria.acresMin, criteria.acresMax, formatAcresValue, ""),
      fields: ["acresMin", "acresMax"],
    });
  }
  chips.push(...describeLayerCriteria(criteria));
  return chips;
}

const WATER_WORDS: Record<string, string> = { any: "Any water", lake: "Lake", river: "River", stream: "Stream", pond: "Pond" };

/** "½ mi", "1 mi", "Crosses it". */
export function tx765Label(miles: number): string {
  if (miles === 0) return "Crosses it";
  const fractions: Record<number, string> = { 0.25: "¼", 0.5: "½", 0.75: "¾" };
  return `${fractions[miles] ?? String(miles)} mi`;
}

function describeLayerCriteria(criteria: StatewideCriteria): CriteriaChip[] {
  const chips: CriteriaChip[] = [];
  if (criteria.water.length > 0) {
    const parts = [criteria.water.map((type) => WATER_WORDS[type] ?? type).join(" or ")];
    if (criteria.waterName.trim()) parts.push(criteria.waterName.trim());
    if (criteria.frontage !== null) parts.push(`${criteria.frontage.toLocaleString("en-US")}+ ft`);
    chips.push({ key: "water", label: "Water", value: parts.join(" · "), fields: ["water", "waterName", "frontage"] });
  }
  if (criteria.flood) {
    chips.push({
      key: "flood",
      label: "Floodplain",
      value: criteria.flood === "in" ? "In it" : "Outside it",
      fields: ["flood"],
    });
  }
  if (criteria.wetland) {
    chips.push({
      key: "wetland",
      label: "Wetland",
      value: criteria.wetland === "none" ? "None mapped" : criteria.wetlandPct ? `${criteria.wetlandPct}%+ of the parcel` : "Some",
      fields: ["wetland", "wetlandPct"],
    });
  }
  if (criteria.tx765 !== null) {
    chips.push({
      key: "tx765",
      label: "765-kV",
      value: criteria.tx765 === 0 ? "Crosses it" : `Within ${tx765Label(criteria.tx765)}`,
      fields: ["tx765"],
    });
  }
  return chips;
}

/** Criteria with these fields cleared, each to its empty value. */
export function clearCriteriaFields(
  criteria: StatewideCriteria,
  fields: readonly (keyof StatewideCriteria)[],
): StatewideCriteria {
  const next = { ...criteria };
  for (const key of fields) (next as Record<string, unknown>)[key] = EMPTY_CRITERIA[key];
  return next;
}

/**
 * Drop the criteria this plan does not include, and say which were dropped.
 *
 * Used on both ends: the worker runs it so a crafted request body cannot reach
 * the gated SQL, and the client runs it on a shared link so the person is told
 * their results are unfiltered rather than silently seeing different ones. The
 * county view's `stripGatedFilters` does the same job for county URLs.
 */
export function stripGatedCriteria(
  criteria: StatewideCriteria,
  plan: Plan,
): { criteria: StatewideCriteria; stripped: string[] } {
  const next = { ...criteria };
  const stripped: string[] = [];

  for (const field of CRITERIA_FIELDS) {
    if (!field.feature || planAllows(plan, field.feature)) continue;
    if (!isFieldSet(criteria, field)) continue;
    (next as Record<string, unknown>)[field.key] = emptyValue(field);
    if (!stripped.includes(field.label)) stripped.push(field.label);
  }

  return { criteria: next, stripped };
}

/**
 * Owner words that search for a circumstance rather than a person, and so
 * build a lead list: estates in probate, heirs, deceased owners. Statewide
 * these are Lite (`searchLeadTerms`); free accounts ran "deceased" and
 * "heirs" across all 72 counties in October 2026, and nobody looking up one
 * owner needs the word, since "smith john" already finds "JOHN SMITH ESTATE".
 *
 * Whole typed words, upper case, as `normalizeOwnerTokens` gives them. The
 * owner box matches at a word start, so the partials that still reach the
 * word are listed too ("ESTA" finds ESTATE); shorter ones ("DEC", "HEI") reach
 * thousands of surnames and are left alone. Not here, on purpose: TRUST and
 * TRUSTEE (368k parcels, mostly families searching their own trust), GUARDIAN
 * and ESTATES (company and subdivision names), and "ET AL" (AL is a name).
 */
const LEAD_TERM_WORDS = new Set([
  "DECE", "DECEA", "DECEAS", "DECEASE", "DECEASED", "DECD", "DECED", "DCSD",
  "HEIR", "HEIRS",
  "EST", "ESTA", "ESTAT", "ESTATE",
  "PROBATE",
  "EXECUTOR", "EXECUTORS", "EXECUTRIX",
  "ADMINISTRATOR", "ADMINISTRATRIX", "ADMR",
  "UNKNOWN",
]);

/**
 * The lead words in an owner search, as typed (upper case); empty when there
 * are none. ESTATE after REAL is a company ("ABC REAL ESTATE LLC", 13,179 of
 * the 19,523 parcels an "estate" search finds), not a decedent's estate.
 * "Personal representative" counts as one term, in either spelling.
 */
export function leadTermsIn(owner: string): string[] {
  const tokens = normalizeOwnerTokens(owner);
  const found: string[] = [];
  tokens.forEach((token, i) => {
    const previous = tokens[i - 1];
    if (LEAD_TERM_WORDS.has(token) && !(token.startsWith("EST") && previous === "REAL")) {
      found.push(token);
    } else if (/^REP/.test(token) && (previous === "PERS" || previous === "PERSONAL")) {
      found.push(`${previous} ${token}`);
    }
  });
  return [...new Set(found)];
}

/**
 * The lead words that keep this plan from running these criteria; empty when
 * it may. Refused outright rather than stripped like a filter: an owner box
 * emptied of "deceased" would run the rest as a different, broader search,
 * and spend a free search on it.
 */
export function lockedLeadTerms(criteria: StatewideCriteria, plan: Plan): string[] {
  return planAllows(plan, "searchLeadTerms") ? [] : leadTermsIn(criteria.owner);
}

/** What a locked lead search tells the person, client and worker alike. */
export function leadTermsMessage(terms: readonly string[]): string {
  const words = terms.map((term) => `"${term.toLowerCase()}"`).join(", ");
  return `Searching statewide for ${words} is a Lite feature. Looking for one owner? Search their name without it.`;
}

/**
 * Whether statewide search reaches owners' mailing addresses for this plan:
 * shows them in results, lists the parcels a typed address matches where the
 * owner gets mail, and runs `mailOf`. All three are `searchMailingStatewide`
 * (Lite). On Free, results carry no mailing address (as the Pro layer facts
 * are left off every other plan's rows), an address search lists only the
 * parcels it matches where the land is and counts the rest
 * (`lockedMailingMatches`), and a `mailOf` search is a count.
 */
export function searchesMailingAddresses(plan: Plan): boolean {
  return planAllows(plan, "searchMailingStatewide");
}

/**
 * True when this plan may not run this `mailOf` search ("other parcels mailed
 * to this owner", statewide). Refused rather than stripped, like a lead term:
 * the rest of the criteria alone would be a different search.
 */
export function lockedMailingSearch(criteria: StatewideCriteria, plan: Plan): boolean {
  return criteria.mailOf !== "" && !searchesMailingAddresses(plan);
}

/** Whether a locked mailing search found anything worth selling. */
export function mailingPreviewWorthShowing(preview: MailingPreview): boolean {
  // The parcel it started from is always among them.
  return preview.parcels > 1;
}

/**
 * What a locked mailing search tells the person, client and worker alike: the
 * count, and that the county's own map lists them free.
 */
export function mailingLockedMessage(preview: MailingPreview): string {
  if (!mailingPreviewWorthShowing(preview)) {
    return "No other parcel statewide is mailed to this owner's address.";
  }
  const parcels = `${preview.parcels.toLocaleString("en-US")} parcels`;
  const counties = preview.counties === 1 ? "1 county" : `${preview.counties.toLocaleString("en-US")} counties`;
  return `${parcels} in ${counties} are mailed to this owner's address. Seeing them statewide is a Lite feature; each county's map lists its own free.`;
}

/** The plan gate behind a label `stripGatedCriteria` reported. */
export function featureOfCriteriaLabel(label: string): Feature | undefined {
  return CRITERIA_FIELDS.find((field) => field.label === label)?.feature;
}

/**
 * A one-line, human-readable rendering of a criteria set, for the search log.
 *
 * Derived from `CRITERIA_FIELDS` rather than written out by hand, so adding or
 * removing a filter cannot leave the log describing a search that no longer
 * exists. Ranges collapse to one `min-max` entry via the field's `group`.
 */
export function summarizeCriteria(criteria: StatewideCriteria): string {
  const parts: string[] = [];
  const seenGroups = new Set<string>();

  for (const field of CRITERIA_FIELDS) {
    if (field.group && field.range) {
      if (seenGroups.has(field.group)) continue;
      const group = CRITERIA_FIELDS.filter((f) => f.group === field.group);
      if (!group.some((f) => isFieldSet(criteria, f))) continue;
      seenGroups.add(field.group);
      const [min, max] = group.map((f) => criteria[f.key] as number | null);
      parts.push(`${field.group}:${min ?? ""}-${max ?? ""}`);
      continue;
    }
    if (!isFieldSet(criteria, field)) continue;
    const value = criteria[field.key];
    parts.push(`${field.param}:${Array.isArray(value) ? value.join("+") : String(value)}`);
  }

  return parts.join(" ") || "(empty)";
}

/**
 * A stable identity for a set of criteria.
 *
 * The daily free-search cap counts *distinct* searches, not HTTP requests, and
 * this is the key it counts. Re-sorting, paging, hitting back, or re-running
 * the same query from a shared link all produce the same key and so cost
 * nothing — which is the only way a 3-a-day cap is livable, because otherwise
 * clicking a column header would burn a search.
 *
 * Deliberately excludes sort and direction: they change the order, never the
 * set. Field order is fixed by `CRITERIA_FIELDS` and counties are sorted, so
 * the key does not depend on how the object was built.
 */
export function criteriaKey(criteria: StatewideCriteria): string {
  const parts: string[] = [];
  for (const field of CRITERIA_FIELDS) {
    if (field.keyWhenSet && !isFieldSet(criteria, field)) continue;
    const value = criteria[field.key];
    if (field.kind === "list") {
      const list = (value as string[]).slice().sort();
      parts.push(`${field.key}=${list.join("+")}`);
    } else if (field.kind === "number") {
      parts.push(`${field.key}=${value === null ? "" : String(value)}`);
    } else {
      parts.push(`${field.key}=${String(value).trim().toLowerCase().replace(/\s+/g, " ")}`);
    }
  }
  return parts.join("&");
}

/**
 * Query tokens for the owner field.
 *
 * Returned as groups: every token must match (AND), and a token matches if any
 * spelling in its group does (OR). Owner names take no street-suffix folding —
 * a COURT surname is not a CT — but a surname particle may be written joined
 * or split (`ownerTokenSpellings`). The typed token is always first.
 */
export function ownerTokenGroups(owner: string): string[][] {
  const tokens = normalizeOwnerTokens(owner);
  return tokens.map((_, i) => ownerTokenSpellings(tokens, i).map((spelling) => spelling.toLowerCase()));
}

/**
 * Query tokens for the address field, with USPS suffix and directional
 * synonyms expanded so "Marlin Court" finds a row stored as "MARLIN CT", and
 * a pasted city/state/ZIP tail dropped, since `site_address` holds none of it.
 */
export function addressTokenGroups(address: string): string[][] {
  // The statewide box only ever holds typed text ("mailed here" is `mailOf`),
  // so a bare trailing city can go. See normalizeAddressQuery.
  const tokens = normalizeAddressQuery(address, { bareCity: true });
  const groups: string[][] = [];
  for (let i = 0; i < tokens.length; i++) {
    // The query side folds every PO box spelling to "PO BOX"; the stored side
    // cannot be folded in a LIKE, so the pair becomes one phrase group with
    // every spelling. One group, not two, or "P O BOX" would need a "po".
    if (tokens[i] === "PO" && tokens[i + 1] === "BOX") {
      // "pob" and "po b" carry a trailing space so the address box's prefix
      // match stays whole-word for them: "% pob%" would find POBANZ RD.
      groups.push(
        PO_BOX_SPELLINGS.map((spelling) => spelling.toLowerCase()).map((spelling) =>
          spelling === "pob" || spelling === "po b" ? `${spelling} ` : spelling,
        ),
      );
      i += 1;
      continue;
    }
    // A route folds to "HWY" and its route on the query side; the stored side
    // has every designation, so the pair becomes one phrase group, ending on
    // a space so "hwy 3" does not also find HWY 33.
    if (tokens[i] === "HWY" && isRouteToken(tokens[i + 1])) {
      const route = tokens[i + 1].toLowerCase();
      groups.push(ROUTE_DESIGNATIONS.map((designation) => `${designation.toLowerCase()} ${route} `));
      i += 1;
      continue;
    }
    const grid = gridSpellings(tokens, i);
    if (grid) {
      groups.push(grid.spellings);
      i = grid.next - 1;
      continue;
    }
    groups.push(expandAddressToken(tokens[i]).map((spelling) => spelling.toLowerCase()));
  }
  return groups;
}

/**
 * A Wisconsin grid address typed with a space the roll does not have: "N 1275
 * Timm Rd" for `N1275 TIMM RD`, "W248 N8875" for `W248N8875`. The address box
 * matches each group at a word start, so "1275" never matched inside "n1275"
 * and 466k parcels (13% of the state) could only be found by typing their
 * number exactly as stored. The joined spelling comes first: it is what the
 * roll usually holds, and `group[0]` is what the house-number checks read.
 * The spaced spelling stays, for the counties that store it that way.
 *
 * Only a lone N/S/E/W directly before a bare number counts, so "1121 S 14th
 * St" is untouched. Returns null when there is nothing to join.
 */
function gridSpellings(tokens: string[], i: number): { spellings: string[]; next: number } | null {
  const parts: string[][] = [];
  let j = i;
  while (parts.length < 2) {
    const token = tokens[j] ?? "";
    const following = tokens[j + 1] ?? "";
    if (/^[NSEW]$/.test(token) && /^\d+$/.test(following)) {
      const joined = `${token}${following}`.toLowerCase();
      parts.push([joined, `${token} ${following}`.toLowerCase()]);
      j += 2;
    } else if (/^[NSEW]\d+$/.test(token)) {
      parts.push([token.toLowerCase()]);
      j += 1;
    } else {
      break;
    }
  }
  // A single part typed as stored ("N1275") needs no help.
  if (parts.length === 0 || (parts.length === 1 && parts[0].length === 1)) return null;
  let spellings = parts[0];
  for (const forms of parts.slice(1)) {
    spellings = spellings.flatMap((prefix) => forms.flatMap((form) => [`${prefix}${form}`, `${prefix} ${form}`]));
  }
  return { spellings: [...new Set(spellings)], next: j };
}

/** Plain word tokens, lowercased — for place names, which take no folding. */
export function plainTokens(text: string): string[] {
  return normalizeOwnerTokens(text).map((token) => token.toLowerCase());
}

// ---------------------------------------------------------------------------
// URL round-tripping
// ---------------------------------------------------------------------------

/**
 * Write criteria into a `URLSearchParams`-shaped bag.
 *
 * Takes and returns plain string pairs rather than a `URLSearchParams` so the
 * worker (which has one) and the tests (which do not need one) agree.
 */
export function criteriaToParams(criteria: StatewideCriteria): Record<string, string> {
  const params: Record<string, string> = {};
  for (const field of CRITERIA_FIELDS) {
    if (!isFieldSet(criteria, field)) continue;
    const value = criteria[field.key];
    params[field.param] = field.kind === "list" ? (value as string[]).join(",") : String(value);
  }
  return params;
}

export function criteriaFromParams(get: (name: string) => string | null): StatewideCriteria {
  const raw: Record<string, unknown> = {};
  for (const field of CRITERIA_FIELDS) {
    const value = get(field.param);
    if (value === null || value === "") continue;
    raw[field.key] = field.kind === "list" ? value.split(",") : value;
  }
  return normalizeCriteria(raw);
}

/** Every URL parameter the search owns — used to clear them on reset. */
export const CRITERIA_PARAMS: readonly string[] = CRITERIA_FIELDS.map((field) => field.param);
